import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { getDate } from 'utils/convertString';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { AppLoader } from 'components/Core/AppLoader';
import BrandCatalogueForm from 'components/Forms/BrandCatalogueForm/BrandCatalogueForm';
import { CataloguesPagesTable } from 'components/Tables/Brand/CataloguesPagesTable/CataloguesPagesTable';
import { BrandsCataloguesTableTransFields } from 'components/Tables/Brand/BrandsCataloguesTable/types';
import { brandsCataloguesTableTranslations } from 'components/Tables/Brand/BrandsCataloguesTable/translations';
import { BrandCataloguesPage } from '../BrandCataloguesPage/BrandCataloguesPage';
import styles from './brandCatalogue.module.scss';

const BrandCatalogue: FC = observer(() => {
  const {
    brandsStore: { getBrandCatalogue, brandsCatalogue, getCataloguePage },
  } = useStore();

  const { t } = useMyTranslations<Extract<BrandsCataloguesTableTransFields, 'createdAt'>>([
    brandsCataloguesTableTranslations.name,
  ]);

  const { catalogueId, pageNumber } = useParams();

  const { isFetching } = useQuery(
    'getBrandCatalogue',
    () => (catalogueId && brandsCatalogue?.id !== catalogueId ? getBrandCatalogue(catalogueId) : {}),
    {
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  const page = useMemo(() => {
    return pageNumber ? getCataloguePage(+pageNumber - 1) : null;
  }, [brandsCatalogue, pageNumber]);

  if (isFetching) return <AppLoader />;
  return brandsCatalogue ? (
    <div>
      <h4 className={styles.header}>
        {brandsCatalogue.name}{' '}
        {brandsCatalogue.createdAt &&
          `${t('createdAt', { ns: brandsCataloguesTableTranslations.name })}: ${getDate(brandsCatalogue.createdAt)}`}
      </h4>
      {pageNumber ? (
        page && <BrandCataloguesPage page={page} />
      ) : (
        <>
          <BrandCatalogueForm />
          <CataloguesPagesTable />
        </>
      )}
    </div>
  ) : null;
});

export default BrandCatalogue;
